import jsPDF from 'jspdf';

// ── Base64 image converter ──────────────────────────────────────────────────
// Fetches image and compresses to max 900px wide at 0.75 quality.
// Phone screenshots are 1170-2532px and cause RangeError in jsPDF when
// many are loaded at full resolution. Canvas resize keeps PDF under 50MB.
async function toBase64(url) {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const blob = await res.blob();
    const originalB64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    // Compress via canvas — skip for PDFs
    if (originalB64.startsWith('data:application/pdf') || originalB64.startsWith('data:image/gif')) {
      return originalB64;
    }
    return await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const MAX_W = 900;
        const scale = img.naturalWidth > MAX_W ? MAX_W / img.naturalWidth : 1;
        const w = Math.round(img.naturalWidth * scale);
        const h = Math.round(img.naturalHeight * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', 0.75));
      };
      img.onerror = () => resolve(originalB64);
      img.src = originalB64;
    });
  } catch (err) {
    console.warn('[PDF] Image load failed:', url, err.message);
    return null;
  }
}

// ── Gray placeholder rectangle ─────────────────────────────────────────────
function drawPlaceholder(doc, x, y, w, h, label) {
  doc.setFillColor(230, 230, 230);
  doc.rect(x, y, w, h, 'F');
  doc.setFontSize(11);
  doc.setTextColor(150, 150, 150);
  doc.text(label, x + w / 2, y + h / 2, { align: 'center', baseline: 'middle' });
}

// ── Red header bar ─────────────────────────────────────────────────────────
function drawHeader(doc, manifestId, clientName, packageId, recipientName, pageNum, totalPages) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  doc.setFillColor(216, 38, 44);
  doc.rect(0, 0, pageW, 28, 'F');
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  doc.text(String(manifestId), 10, 7);
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text(`${clientName}  •  ${packageId}`, pageW / 2, 13, { align: 'center' });
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.text(String(recipientName), pageW / 2, 22, { align: 'center' });
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text(`${pageNum} / ${totalPages}`, pageW - 10, pageH - 5, { align: 'right' });
}

// ── Fit image into box preserving aspect ratio ─────────────────────────────
function fitInBox(imgW, imgH, boxW, boxH) {
  const scale = Math.min(boxW / imgW, boxH / imgH);
  return { w: imgW * scale, h: imgH * scale };
}

// ── Grid layout for receipts page (preserved for future callers) ──────────
// eslint-disable-next-line no-unused-vars
function gridFor(count) {
  if (count <= 1) return { cols: 1, rows: 1 };
  if (count === 2) return { cols: 1, rows: 2 };
  if (count <= 4) return { cols: 2, rows: 2 };
  if (count <= 6) return { cols: 2, rows: 3 };
  if (count <= 9) return { cols: 3, rows: 3 };
  if (count <= 12) return { cols: 3, rows: 4 };
  if (count <= 15) return { cols: 3, rows: 5 };
  return { cols: 4, rows: 5 };
}

// ── Pre-fetch all images in parallel batches before building PDF ───────────
// Separates async IO from synchronous CPU (jsPDF.addImage is CPU-heavy).
// Returns url→base64 map. Batches of 5 prevent browser memory overflow.
async function prefetchImages(urls, batchSize = 5) {
  const unique = [...new Set(urls.filter(Boolean))];
  const map = {};
  for (let i = 0; i < unique.length; i += batchSize) {
    const batch = unique.slice(i, i + batchSize);
    const results = await Promise.all(batch.map(url => toBase64(url)));
    batch.forEach((url, j) => { map[url] = results[j]; });
  }
  return map;
}

// ── Phase A+B: shared data fetch + normalize ───────────────────────────────
async function fetchPackages(manifestId, supabase) {
  const [shipmentsRes, encomiendaRes] = await Promise.all([
    supabase
      .from('shipments')
      .select('id, unique_package_id, customer_id, recipient_name, warehouse_photo_url, photo_url, entry_timestamp, customers(id, full_name)')
      .eq('manifest_id', manifestId)
      .order('entry_timestamp', { ascending: true }),
    supabase
      .from('encomiendas')
      .select('id, unique_encomienda_id, customer_id, customers(id, full_name), encomienda_packages(id, unique_package_id, recipient_name, package_number, photo_url)')
      .eq('manifest_id', manifestId)
      .eq('status', 'COMPLETE')
      .order('unique_encomienda_id', { ascending: true }),
  ]);

  const casilleroShipments = shipmentsRes.data || [];
  const encomiendas = encomiendaRes.data || [];

  const casilleroIds = casilleroShipments.map(s => s.id);
  const encPkgIds = encomiendas.flatMap(enc => (enc.encomienda_packages || []).map(p => p.id));

  const [casilleroReceiptsRes, encReceiptsRes] = await Promise.all([
    supabase
      .from('package_receipts')
      .select('id, shipment_id, receipt_url')
      .in('shipment_id', casilleroIds.length > 0 ? casilleroIds : ['__none__']),
    supabase
      .from('package_receipts')
      .select('id, encomienda_package_id, receipt_url')
      .in('encomienda_package_id', encPkgIds.length > 0 ? encPkgIds : ['__none__']),
  ]);

  const receiptsByShipment = {};
  for (const r of casilleroReceiptsRes.data || []) {
    if (!receiptsByShipment[r.shipment_id]) receiptsByShipment[r.shipment_id] = [];
    receiptsByShipment[r.shipment_id].push(r.receipt_url);
  }

  const receiptsByEncPkg = {};
  for (const r of encReceiptsRes.data || []) {
    if (!receiptsByEncPkg[r.encomienda_package_id]) receiptsByEncPkg[r.encomienda_package_id] = [];
    receiptsByEncPkg[r.encomienda_package_id].push(r.receipt_url);
  }

  const packages = [];

  for (const s of casilleroShipments) {
    packages.push({
      id: s.id,
      packageId: s.unique_package_id || s.id,
      clientName: s.customers?.full_name || s.customer_id || 'Cliente',
      recipientName: s.recipient_name || s.unique_package_id || s.id,
      photoUrl: s.photo_url ?? s.warehouse_photo_url ?? null,
      receipts: (receiptsByShipment[s.id] || []).slice(0, 20),
      type: 'CASILLERO',
    });
  }

  for (const enc of encomiendas) {
    const clientName = enc.customers?.full_name || enc.customer_id || 'Cliente';
    for (const p of enc.encomienda_packages || []) {
      packages.push({
        id: p.id,
        packageId: p.unique_package_id || p.id,
        clientName,
        recipientName: p.recipient_name || p.unique_package_id || p.id,
        photoUrl: p.photo_url || null,
        receipts: (receiptsByEncPkg[p.id] || []).slice(0, 20),
        type: 'ENCOMIENDA',
      });
    }
  }

  packages.sort((a, b) => {
    const nameOrder = a.clientName.localeCompare(b.clientName);
    if (nameOrder !== 0) return nameOrder;
    return a.packageId.localeCompare(b.packageId);
  });

  return packages;
}

// ── PDF 1: Fotos ───────────────────────────────────────────────────────────
export async function generateFotosPDF(manifestId, supabase) {
  const packages = await fetchPackages(manifestId, supabase);
  if (packages.length === 0) return;

  const withPhoto = packages.filter(p => p.photoUrl).length;
  console.log(`[PDF Fotos] manifest=${manifestId} packages=${packages.length} withPhoto=${withPhoto}`);

  // Pre-fetch all photos before building PDF
  const photoUrls = packages.map(p => p.photoUrl).filter(Boolean);
  const imageMap = await prefetchImages(photoUrls, 5);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const bodyX = 10;
  const bodyY = 38;
  const bodyW = pageW - 20;
  const bodyH = pageH - bodyY - 20;
  const total = packages.length;

  for (let i = 0; i < packages.length; i++) {
    const pkg = packages[i];
    if (i > 0) doc.addPage();
    drawHeader(doc, manifestId, pkg.clientName, pkg.packageId, pkg.recipientName, i + 1, total);

    const b64 = pkg.photoUrl ? (imageMap[pkg.photoUrl] || null) : null;
    let drawn = false;

    if (b64) {
      const dims = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = () => resolve(null);
        img.src = b64;
      });
      if (dims && dims.w > 0 && dims.h > 0) {
        const { w, h } = fitInBox(dims.w, dims.h, bodyW, bodyH);
        const imgX = bodyX + (bodyW - w) / 2;
        const imgY = bodyY + (bodyH - h) / 2;
        const fmt = b64.startsWith('data:image/png') ? 'PNG' : 'JPEG';
        doc.addImage(b64, fmt, imgX, imgY, w, h);
        drawn = true;
      }
    }
    if (!drawn) {
      drawPlaceholder(doc, bodyX, bodyY, bodyW, bodyH, 'Sin foto registrada');
    }
  }

  doc.save(`MAN-${manifestId}-fotos.pdf`);
}

// ── PDF 2: Recibos ─────────────────────────────────────────────────────────
export async function generateRecibosPDF(manifestId, supabase) {
  const packages = await fetchPackages(manifestId, supabase);
  if (packages.length === 0) return;

  const totalReceipts = packages.reduce((sum, p) => sum + (p.receipts?.length || 0), 0);
  const withReceipts = packages.filter(p => (p.receipts?.length || 0) > 0).length;
  console.log(`[PDF Recibos] manifest=${manifestId} packages=${packages.length} withReceipts=${withReceipts} totalImages=${totalReceipts}`);

  // PHASE 1: Pre-fetch ALL receipt images before touching jsPDF
  const allReceiptUrls = packages.flatMap(p => p.receipts || []);
  const imageMap = await prefetchImages(allReceiptUrls, 5);
  console.log(`[PDF Recibos] pre-fetched ${Object.keys(imageMap).length} unique images`);

  // PHASE 2: Build PDF synchronously using pre-fetched map
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const bodyX = 10;
  const bodyY = 38;
  const bodyW = pageW - 20;
  const availH = pageH - bodyY - 10;

  // True total page count: ≥1 page per package, ceil(receipts/4) when receipts exist
  const totalPages = packages.reduce(
    (sum, p) => sum + Math.max(1, Math.ceil((p.receipts?.length || 0) / 4) || 1),
    0
  );

  // Fixed 2×2 grid — hard cap of 4 receipts per page
  const cols = 2;
  const rows = 2;
  const gutter = 3;
  const cellW = (bodyW - gutter * (cols - 1)) / cols;
  const cellH = (availH - gutter * (rows - 1)) / rows;

  let pageNum = 0;
  let isFirstPage = true;

  for (let i = 0; i < packages.length; i++) {
    const pkg = packages[i];
    const receipts = (pkg.receipts || []).slice(0, 20);

    if (receipts.length === 0) {
      // One placeholder page per package with no receipts
      if (!isFirstPage) doc.addPage();
      isFirstPage = false;
      pageNum++;
      drawHeader(doc, manifestId, pkg.clientName, pkg.packageId, pkg.recipientName, pageNum, totalPages);
      drawPlaceholder(doc, bodyX, bodyY, bodyW, availH, 'Sin recibos adjuntos');
    } else {
      const pkgPages = Math.ceil(receipts.length / 4);

      for (let p = 0; p < pkgPages; p++) {
        if (!isFirstPage) doc.addPage();
        isFirstPage = false;
        pageNum++;

        drawHeader(doc, manifestId, pkg.clientName, pkg.packageId, pkg.recipientName, pageNum, totalPages);

        // Continuation indicator — only shown when this package spans multiple pages
        if (pkgPages > 1) {
          doc.setFontSize(8);
          doc.setTextColor(255, 255, 255);
          doc.text(`Recibos — Página ${p + 1} / ${pkgPages}`, pageW / 2, 25, { align: 'center' });
        }

        const pageReceipts = receipts.slice(p * 4, p * 4 + 4);

        for (let j = 0; j < pageReceipts.length; j++) {
          const col = j % cols;
          const row = Math.floor(j / cols);
          const cellX = bodyX + col * (cellW + gutter);
          const cellY = bodyY + row * (cellH + gutter);
          const b64 = imageMap[pageReceipts[j]] || null;

          if (b64) {
            const dims = await new Promise((resolve) => {
              const img = new Image();
              img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
              img.onerror = () => resolve(null);
              img.src = b64;
            });
            if (dims && dims.w > 0 && dims.h > 0) {
              const { w, h } = fitInBox(dims.w, dims.h, cellW, cellH);
              const imgX = cellX + (cellW - w) / 2;
              const imgY = cellY + (cellH - h) / 2;
              const fmt = b64.startsWith('data:image/png') ? 'PNG' : 'JPEG';
              doc.addImage(b64, fmt, imgX, imgY, w, h);
            } else {
              drawPlaceholder(doc, cellX, cellY, cellW, cellH, 'Sin imagen');
            }
          } else {
            drawPlaceholder(doc, cellX, cellY, cellW, cellH, 'Sin imagen');
          }
        }
      }
    }
  }

  doc.save(`MAN-${manifestId}-recibos.pdf`);
}
